import { Area, CoordinateSystem, type IsoLinesRequest } from "@mm/api.meteomatics.com";
import type {
  IsoBandsLayer as ApiIsoBandsLayer,
  IsoBandsLayerCreate as ApiIsoBandsLayerCreate,
  IsoLinesLayer as ApiIsoLinesLayer,
  IsoLinesLayerCreate as ApiIsoLinesLayerCreate,
  IsoBandsLayerCustomOptions,
  IsoLinesLayerCustomOptions,
} from "@mm/metx-workbench.meteomatics.com";

export type IsoLayerCustomOptions = IsoBandsLayerCustomOptions & IsoLinesLayerCustomOptions;
export type IsoBandsLayer = ApiIsoBandsLayer;
export type IsoBandsLayerCreate = ApiIsoBandsLayerCreate;
export type IsoLinesLayer = ApiIsoLinesLayer;
export type IsoLinesLayerCreate = ApiIsoLinesLayerCreate;

type BuildIsoRequestInput = {
  path: IsoLinesRequest["path"];
  area: IsoLinesRequest["area"];
  parameter: IsoLinesRequest["parameter"];
  model: IsoLinesRequest["model"];
  ensSelect?: IsoLinesRequest["ensSelect"] | null;
  boundingBoxLimit: IsoLinesRequest["boundingBoxLimit"];
  datetime: IsoLinesRequest["datetime"];
  height: IsoLinesRequest["height"];
  width: IsoLinesRequest["width"];
  radiusGaussianFilter?: IsoLinesRequest["radiusGaussianFilter"];
  radiusMedianFilter?: IsoLinesRequest["radiusMedianFilter"];
  values?: string | null;
  valueRange?: string | null;
  customOptions?: unknown;
  calibrated?: boolean | null;
};

type IsoLayerCustomOptionsExperimental = IsoLayerCustomOptions & {
  api_url_suffix?: string;
};

const asObject = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === "object" && value !== null) {
    return value as Record<string, unknown>;
  }
  return null;
};

const pickOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

export function readIsoLayerCustomOptions(customOptions: unknown): IsoLayerCustomOptions {
  const options = asObject(customOptions);
  if (!options) {
    return {};
  }
  return {
    fill_color: pickOptionalString(options.fill_color),
    fill_colors: pickOptionalString(options.fill_colors),
    fill_color_mode: pickOptionalString(options.fill_color_mode),
    fill_reverse: pickOptionalString(options.fill_reverse),
    isoband_intervals: pickOptionalString(options.isoband_intervals),
    fixed_bbox: pickOptionalString(options.fixed_bbox),
    resolution: pickOptionalString(options.resolution),
    api_url_suffix: pickOptionalString(options.api_url_suffix),
  } as IsoLayerCustomOptions;
}

export function parseFixedBBoxAreaFromCustomOptions(customOptions: unknown): IsoLinesRequest["area"] | null {
  const fixedBBox = readIsoLayerCustomOptions(customOptions).fixed_bbox;
  if (!fixedBBox) {
    return null;
  }

  const coordinates = fixedBBox.split(",").map((value) => Number(value.trim()));
  if (coordinates.length !== 4 || coordinates.some((value) => !Number.isFinite(value))) {
    return null;
  }

  const [lngMin, latMin, lngMax, latMax] = coordinates;
  if (lngMin >= lngMax || latMin >= latMax) {
    return null;
  }

  return new Area(CoordinateSystem.WGS84, {
    latMin,
    latMax,
    lngMin,
    lngMax,
  });
}

function selectExclusiveIsoBandAndLineParams(params: {
  isobandIntervals?: string | null;
  isolineValues?: string | null;
  isolineRange?: string | null;
}) {
  const isobandIntervals = pickOptionalString(params.isobandIntervals);
  if (isobandIntervals) {
    return {
      isobandIntervals,
      isolineValues: undefined,
      isolineRange: undefined,
    };
  }

  const isolineValues = pickOptionalString(params.isolineValues);
  if (isolineValues) {
    return {
      isobandIntervals: undefined,
      isolineValues,
      isolineRange: undefined,
    };
  }

  return {
    isobandIntervals: undefined,
    isolineValues: undefined,
    isolineRange: pickOptionalString(params.isolineRange),
  };
}

export function buildIsoLinesRequest(input: BuildIsoRequestInput): IsoLinesRequest {
  const customOptions = readIsoLayerCustomOptions(input.customOptions);
  const exclusiveParams = selectExclusiveIsoBandAndLineParams({
    isobandIntervals: customOptions.isoband_intervals,
    isolineValues: input.values ?? undefined,
    isolineRange: input.valueRange ?? undefined,
  });

  return {
    path: input.path,
    area: input.area,
    parameter: input.parameter,
    model: input.model,
    ensSelect: input.ensSelect ? input.ensSelect : "",
    boundingBoxLimit: input.boundingBoxLimit,
    datetime: input.datetime,
    height: input.height,
    width: input.width,
    radiusGaussianFilter: input.radiusGaussianFilter,
    radiusMedianFilter: input.radiusMedianFilter,
    isobandIntervals: exclusiveParams.isobandIntervals,
    isolineRange: exclusiveParams.isolineRange,
    isolineValues: exclusiveParams.isolineValues,
    resolution: customOptions.resolution ?? undefined,
    calibrated: !!input.calibrated,
    apiUrlSuffix: (customOptions as IsoLayerCustomOptionsExperimental).api_url_suffix,
  };
}
